import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Box,
  Button,
  Collapse,
  Container,
  debounce,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import {
  ArrowBack,
  ArrowForward,
  BookOnline,
  ExpandLess,
  ExpandMore,
  RestartAlt,
} from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { styled } from "@mui/material/styles";
import Form from "@rjsf/mui";
import validator from "@rjsf/validator-ajv8";
import {
  clientInfoSchema,
  serviceScheduleSchema,
  serviceDetailSchema,
  serviceAddressSchema,
  careStatusSchema,
  authContactSchema,
  emergencyPlaningSchema,
  documentationSchema,
  paymentDetailSchema,
  billingInfoSchema,
  termsAndConditionSchema,
  reviewSchema,
  clientInfoUiSchema,
  serviceScheduleUiSchema,
  serviceDetailUiSchema,
  serviceAddressUiSchema,
  careStatusUiSchema,
  authContactUiSchema,
  emergencyPlaningUiSchema,
  documentationUiSchema,
  paymentDetailUiSchema,
  billingInfoUiSchema,
  termsAndConditionUiSchema,
  reviewUiSchema,
} from "../../steps";
import { FillerIdentityWidget } from "../../step-components/FillerIdentityWidget";
import { CommunityLiaisonField } from "../../step-components/CommunityLiaisonField";
import ScheduleTimesWidget from "../../step-components/ScheduleTimesWidget";
import CardCheckboxWidget from "../../step-components/CardCheckboxWidget";
import PetDetailsField from "../../step-components/PetDetailsField";
import CollapsibleContactField from "../../step-components/CollapsibleContactField";
import EmergencyPlanningField from "../../step-components/EmergencyPlanningField";
import DocumentationField from "../../step-components/DocumentationField";
import ReviewPdfField from "../../step-components/ReviewPdfField";
import { openDB } from "idb";
import CryptoJS from "crypto-js";
import AdornmentTextWidget from "../../step-components/AdornmentTextWidget";
import RadioDescriptionWidget from "../../step-components/RadioDescriptionWidget";
import DateWidget from "../../step-components/DateWidget";
import CityField from "../../step-components/CityField";
import apiClient from "../../lib/api";
import urlConstant from "../../constants/urlConstants";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import { lightPalette } from "../../theme-layout/palette";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import form_datatype from "../../constants/formDataType";
import dataConstant from "../../constants/dataConstants";
import { trackPromise } from "react-promise-tracker";
import { useSelector } from "react-redux";
import { selectUserDetail } from "../../redux/slice/userSlice";
import SignatureWidget from "../../step-components/SignatureWidget";
import { handleDependencies } from "../../step-components/dependencyUtils";
import StateField from "../../step-components/StateField";
import RelationshipSelect from "../../step-components/RelationshipSelect";
import ContactDropdownField from "../../step-components/ContactDropdownField";
import PhoneWidget from "../../step-components/PhoneWidget";
import NumberWidget from "../../step-components/NumberWidget";
import SnackState from "../../shared-components/snackbar/snackState";
import CommonSnackBar from "../../shared-components/snackbar/commonSnackbar";
import dependencyArray from "../../step-components/dependencyKeys";
import TextareaAutosizeWidget from "../../step-components/TextareaAutosizeWidget";
import AgreementCheckboxField from "../../step-components/AgreementCheckboxField";
import MarketerCodeVerify from "./marketerCodeVerify";
import MultiSelectDropdownWidget from "../../step-components/MultiSelectDropdownWidget";
import MultiSelectSearchDropdownWidget from "../../step-components/MultiSelectSearchDropdownWidget";
import { requiredFieldsValidate } from "../../components/enum/formEnum";
import DescriptionField from "../../step-components/DescriptionField";
import ImageWidget from "../../step-components/ImageWidget";

const schemas = [
  clientInfoSchema,
  serviceScheduleSchema,
  serviceDetailSchema,
  serviceAddressSchema,
  careStatusSchema,
  authContactSchema,
  emergencyPlaningSchema,
  documentationSchema,
  paymentDetailSchema,
  billingInfoSchema,
  termsAndConditionSchema,
  reviewSchema,
];

const uiSchemas = [
  clientInfoUiSchema,
  serviceScheduleUiSchema,
  serviceDetailUiSchema,
  serviceAddressUiSchema,
  careStatusUiSchema,
  authContactUiSchema,
  emergencyPlaningUiSchema,
  documentationUiSchema,
  paymentDetailUiSchema,
  billingInfoUiSchema,
  termsAndConditionUiSchema,
  reviewUiSchema,
];

const stepTitles = [
  "Client Info",
  "Service Schedule",
  "Service Details",
  "Service Address",
  "Care Status",
  "Authorized Contacts",
  "Emergency Planning",
  "Documentation",
  "Payment Details",
  "Billing Info",
  "Terms & Conditions",
  "Review & Submit",
];

function decrypt(encryptedValue, key) {
  try {
    const type = form_datatype[key];
    const decrypted = CryptoJS.AES.decrypt(
      encryptedValue,
      process.env.REACT_APP_ENCRYPTION_KEY,
    );
    let plaintext = decrypted.toString(CryptoJS.enc.Utf8);
    if (!plaintext) return encryptedValue;
    switch (type) {
      case "boolean":
        return plaintext === "true";
      case "number":
        const num = Number(plaintext);
        return isNaN(num) ? encryptedValue : num;
      case "string":
      default:
        return plaintext;
    }
  } catch {
    return encryptedValue;
  }
}

function decryptObject(obj, parentKey = "") {
  if (Array.isArray(obj)) {
    return obj.map((item) => decryptObject(item, parentKey)); // pass down parentKey
  } else if (typeof obj === "object" && obj !== null) {
    const result = {};
    for (const key in obj) {
      const fullKey = key; // or `${parentKey}.${key}` if you want full path
      result[key] = decryptObject(obj[key], fullKey);
    }
    return result;
  } else if (typeof obj === "string") {
    return decrypt(obj, parentKey);
  } else {
    return obj;
  }
}

const FormCard = styled(Paper)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: "rgba(255, 255, 255, 0.25)",
  borderRadius: 16,
}));

const dbPromise = openDB("ClientIntakeFormDB", 1, {
  upgrade(db) {
    if (!db.objectStoreNames.contains("formData")) {
      db.createObjectStore("formData", { keyPath: "id" });
    }
  },
});

const deepMerge = (target, source) => {
  const output = { ...target };
  Object.keys(source).forEach((key) => {
    if (
      source[key] &&
      typeof source[key] === "object" &&
      !Array.isArray(source[key])
    ) {
      output[key] = deepMerge(target[key] || {}, source[key]);
    } else {
      output[key] = source[key];
    }
  });
  return output;
};

const getOrderedFields = (schema, properties, formData) => {
  if (!schema?.dependencies) return properties;

  const ordered = [];
  const added = new Set();
  const propertyMap = Object.fromEntries(properties.map((p) => [p.name, p]));

  for (const key of Object.keys(schema.properties)) {
    if (!propertyMap[key]) continue;
    ordered.push(propertyMap[key]);
    added.add(key);

    const dep = schema.dependencies[key];
    if (dep?.oneOf && formData[key]) {
      const value = formData[key];
      dep.oneOf.forEach((option) => {
        if (option.properties[key]?.const === value || value === true) {
          const depKeys = Object.keys(option.properties || {}).filter(
            (k) => k !== key && !added.has(k),
          );
          depKeys.forEach((dk) => {
            if (propertyMap[dk] && !added.has(dk)) {
              ordered.push(propertyMap[dk]);
              added.add(dk);
            }
          });
        }
      });
    }
  }

  properties.forEach((p) => {
    if (!added.has(p.name)) ordered.push(p);
  });

  return ordered;
};

const ObjectFieldTemplate = ({ properties, uiSchema, schema, formData }) => {
  const orderedProperties = getOrderedFields(schema, properties, formData);
  return (
    <Grid container spacing={2}>
      {orderedProperties.map((element, index) => (
        <Grid
          item
          xs={12}
          md={uiSchema[element.name]?.["ui:md"] || 12}
          key={index}
        >
          {element.content}
        </Grid>
      ))}
    </Grid>
  );
};
const IntakeValidate = (formData, errors) => {
  errors.clientInformation = errors.clientInformation || {};
  return errors;
};

function diffObjects(obj1, obj2) {
  const changes = {};
  const allKeys = new Set([...Object.keys(obj1), ...Object.keys(obj2)]);

  for (const key of allKeys) {
    let val1 = obj1[key];
    let val2 = obj2[key];

    const type = form_datatype[key];

    // Handle undefined default values
    if (val1 === undefined) {
      if (type === "boolean") val1 = false;
      else if (Array.isArray(val1)) val1 = [];
      else if (type === "string" || !type) val1 = "";
    }
    if (val2 === undefined) {
      if (type === "boolean") val2 = false;
      else if (Array.isArray(val2)) val2 = [];
      else if (type === "string" || !type) val2 = "";
    }

    // Handle arrays
    if (Array.isArray(val1) && Array.isArray(val2)) {
      if (JSON.stringify(val1) !== JSON.stringify(val2)) {
        changes[key] = { from: val1, to: val2 };
      }

      // Handle nested objects
    } else if (
      typeof val1 === "object" &&
      typeof val2 === "object" &&
      val1 &&
      val2
    ) {
      const childDiff = diffObjects(val1, val2);
      if (Object.keys(childDiff).length > 0) {
        changes[key] = childDiff;
      }

      // Handle primitive values
    } else if (val1 !== val2) {
      changes[key] = { from: val1, to: val2 };
    }
  }

  return changes;
}

function cleanDisabledDependents(formData, schema) {
  const cleaned = { ...formData };
  if (!schema.dependencies) return cleaned;

  for (const key in schema.dependencies) {
    const dep = schema.dependencies[key];

    // Handle 'oneOf' dependency
    if (Array.isArray(dep.oneOf)) {
      const matched = dep.oneOf.find((option) => {
        const expected = option.properties?.[key]?.const;
        return cleaned[key] === expected;
      });

      // Keep only allowed keys from the matched schema
      const allowedKeys = new Set([
        key,
        ...Object.keys(matched?.properties || {}),
      ]);

      for (const k in cleaned) {
        if (!allowedKeys.has(k)) {
          delete cleaned[k];
        }
      }
    }

    // Handle array-based boolean dependencies (if key is false, remove its dependents)
    if (Array.isArray(dep) && !cleaned[key]) {
      for (const depKey of dep) {
        delete cleaned[depKey];
      }
    }
  }

  return cleaned;
}

const ClientIntakeFrom = () => {
  const { id } = useParams();
  const getUserdetail = useSelector(selectUserDetail);
  const [snack, closeSnack, showSnackbar] = SnackState();
  const navigate = useNavigate();
  // Initialize `pendingSteps` with all step indices except 7 and 11,
  // because steps 7 ("Billing Info") and 11 ("Review") contain no required fields
  // and do not need to be validated before progressing.
  const [pendingSteps, setPendingSteps] = useState(
    Array.from({ length: schemas.length }, (_, i) => i).filter(
      (i) => i !== 7 && i !== 11,
    ),
  );
  const defaultDaysMap = {
    Custom: [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
    ],
    "24/7 Round-the-Clock Care": [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
    ],
    "Live-in Care": [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
    ],
  };

  const defaultFormData = {
    scheduleVariableFlexible: {
      scheduleType: "",
      days: defaultDaysMap[""],
      showTimes: true,
      dayTimes: {},
    },
    serviceDetails: {
      startDate: "",
      caregiverPreference: "",
      specialRequests: "",
    },
    contacts: [],
    billingInfoSection0: {},
    insurance: {
      insuranceTerm: {
        agreed: null,
        policyDeliveryOption: "",
        policyDocuments: [],
      },
    },
  };

  const [formData, setFormData] = useState(defaultFormData);
  const [step, setStep] = useState(0);
  const [openSections, setOpenSections] = useState({});
  const [extraErrors, setExtraErrors] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const stepperRef = useRef(null);
  const [watchCity, setWatchCity] = useState("");
  const [cityLiaisonMapping, setCityLiaisonMapping] = useState({});
  const [isCityLiaisonMappingCalled, setIsCityLiaisonMappingCalled] =
    useState(false);
  const [formDataVersion, setFormDataVersion] = useState(0);
  const [formCustomValidation, setCustomValidation] = useState(true);
  const [isCodeValidPopupOpen, setIsCodeValidPopupOpen] = useState(!id);
  const [marketerCode, setMarketerCode] = useState("");
  const [oldFormData, setOldFormData] = useState();
  const progress = ((step + 1) / schemas.length) * 100;
  const location = useLocation();
  const [policyFiles, setPolicyFiles] = useState([]);

  const widgets = {
    FillerIdentityWidget,
    CardCheckboxWidget,
    AdornmentTextWidget,
    DateWidget: DateWidget,
    RadioDescriptionWidget,
    SignatureWidget,
    RelationshipSelect,
    PhoneWidget,
    NumberWidget,
    TextareaAutosizeWidget,
    MultiSelectDropdown: MultiSelectDropdownWidget,
    MultiSelectSearchDropdownWidget,
    ImageWidget,
  };

  const fields = {
    CommunityLiaisonField,
    ScheduleTimesWidget,
    PetDetailsField,
    CollapsibleContactField,
    EmergencyPlanningField,
    DocumentationField,
    ReviewPdfField,
    CityField,
    StateField,
    ContactDropdownField,
    AgreementCheckboxField,
    DescriptionField,
  };

  const formContext = {
    watchCity,
    setWatchCity,
    cityLiaisonMapping,
    setCityLiaisonMapping,
    setIsCityLiaisonMappingCalled,
    isCityLiaisonMappingCalled,
    formData,
    policyFiles,
    setPolicyFiles,
  };
  useEffect(() => {
    const scrollAllowedPrefixes = [
      "/clientIntake",
      "/clientintake",
      "/manageSubmission/",
    ];

    const isScrollAllowed = scrollAllowedPrefixes.some((prefix) =>
      location.pathname.startsWith(prefix),
    );

    document.body.style.overflow = isScrollAllowed ? "auto" : "hidden";

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [location.pathname]);

  useEffect(() => {
    const scheduleType = formData.scheduleVariableFlexible?.scheduleType;
    const previousDays = formData.scheduleVariableFlexible?.dayTimes?.days;
    const allowedDays = defaultDaysMap[scheduleType];
    if (!allowedDays) return;

    setFormData((prev) => {
      const prevDayTimes = prev.scheduleVariableFlexible?.dayTimes || {};
      const fullDayTimes = prevDayTimes.dayTimes || {};
      const finalDays = previousDays ?? allowedDays;
      const filteredDayTimes = Object.fromEntries(
        Object.entries(fullDayTimes).filter(([day]) => finalDays.includes(day)),
      );

      const showTimes = !["24/7 Round-the-Clock Care", "Live-in Care"].includes(
        scheduleType,
      );

      return {
        ...prev,
        scheduleVariableFlexible: {
          ...prev.scheduleVariableFlexible,
          days: finalDays,
          showTimes,
          dayTimes: {
            days: finalDays,
            showTimes,
            dayTimes: filteredDayTimes,
          },
        },
      };
    });
  }, [formData.scheduleVariableFlexible?.scheduleType]);

  useEffect(() => {
    async function loadFormData() {
      try {
        const db = await dbPromise;
        const savedData = await db.get("formData", "clientIntake");

        if (savedData?.data) {
          setFormData(deepMerge(defaultFormData, savedData.data));
        }

        if (savedData?.pendingSteps) {
          setPendingSteps(savedData.pendingSteps);
        } else {
          // Initialize all steps as pending except step 7 and 11 (they are optional/skipped)
          const stepsToValidate = Array.from(
            { length: schemas.length },
            (_, i) => i,
          );
          const filteredSteps = stepsToValidate.filter(
            (i) => i !== 7 && i !== 11,
          );
          setPendingSteps(filteredSteps);
        }
      } catch (error) {
        console.error("Error loading form data from IndexedDB:", error);
      }
    }

    // Only load data if it's a new form (not editing an existing one)
    if (!id) {
      loadFormData();
    }
  }, []);

  useEffect(() => {
    if (id) {
      try {
        trackPromise(
          apiClient
            .get(
              `${urlConstant.Submission.GetSubmissionById}?submissionUnqGuid=${id}`,
              {
                authRequired: true,
              },
            )
            .then(async (response) => {
              if (response.status !== dataConstant.APIStatus.Success) {
                showSnackbar(response?.message, "error");
              } else {
                const result = await decryptObject(
                  response.data.formData.form_data,
                );

                result.insurance = result.insurance || {};
                result.insurance.insuranceTerm =
                  result.insurance.insuranceTerm || {};

                if (response?.data?.uploadPolicyDocumentResponses.length > 0) {
                  result.insurance.insuranceTerm.policyDocuments =
                    response?.data?.uploadPolicyDocumentResponses?.map(
                      (doc) => ({
                        name: doc.fileName,
                        size: doc.fileSize,
                        type: doc.fileType,
                        filePath: doc.filePath,
                        isExisting: true,
                        content: null,
                      }),
                    );
                }

                if (
                  result &&
                  !result?.overtimeAgreement?.overtimeAgreementTerm &&
                  Boolean(result?.paymentDetailsSection0?.overtimeAgreement) ===
                    true
                ) {
                  result.overtimeAgreement = {
                    ...result.overtimeAgreement,
                    overtimeAgreementTerm: Boolean(
                      result.paymentDetailsSection0.overtimeAgreement,
                    ),
                  };
                }
                result.createdAt = response.data?.createdAt;
                setOldFormData(result);
                setFormData(deepMerge(defaultFormData, result));
              }
            }),
        );
      } catch (error) {
        console.error("Error loading form data from IndexedDB:", error);
      }
    }
  }, []);

  useEffect(() => {
    const openState = {};
    schemas.forEach((s, i) => {
      Object.keys(s.properties || {}).forEach((k) => {
        openState[`${i}-${k}`] = true;
      });
    });
    setOpenSections(openState);
  }, []);

  useEffect(() => {
    const { fillersFirstName, fillersLastName, isFillingForSomeoneElse } =
      formData.formSubmissionDetails || {};

    if (
      isFillingForSomeoneElse === "No" &&
      (fillersFirstName || fillersLastName)
    ) {
      setFormData((prev) => ({
        ...prev,
        clientInformation: {
          ...prev.clientInformation,
          firstName: prev.formSubmissionDetails.fillersFirstName,
          lastName: prev.formSubmissionDetails.fillersLastName,
        },
      }));
    }
  }, [
    formData.formSubmissionDetails?.fillersFirstName,
    formData.formSubmissionDetails?.fillersLastName,
    formData.formSubmissionDetails?.isFillingForSomeoneElse,
  ]);

  const stepSchemaSections = useMemo(() => {
    return schemas.map((schema) => Object.keys(schema.properties || {}));
  }, [schemas]);
  const currentSchemas = useMemo(() => {
    const schema = schemas[step] || { properties: {} };
    return schema.properties || {};
  }, [step, schemas]);

  const currentUiSchemas = useMemo(() => {
    const uiSchema = uiSchemas[step] || {};
    return uiSchema;
  }, [step, uiSchemas]);

  // Synchronous validation
  const validateForm = (sectionKey) => {
    const result = validator.validateFormData(
      formData[sectionKey] || {},
      currentSchemas[sectionKey],
      IntakeValidate,
      transformErrors,
      currentUiSchemas[sectionKey],
    );
    if (result.errors.length > 0) {
      const sectionErrors = {};
      result.errors.forEach((err) => {
        const path =
          err.property?.replace(/^\./, "").split(".").filter(Boolean) || [];
        if (path.length) {
          let cursor = sectionErrors;
          path.forEach((key, idx) => {
            if (key.startsWith("[") && key.endsWith("]")) {
              key = key.slice(1, -1);
            }
            cursor[key] =
              cursor[key] || (idx === path.length - 1 ? { __errors: [] } : {});
            if (idx === path.length - 1) {
              if (!cursor[key].__errors?.includes(err?.message)) {
                cursor[key].__errors = [
                  ...(cursor[key].__errors || []),
                  err?.message,
                ];
              }
            }
            cursor = cursor[key];
          });
        }
      });
      return { valid: false, errors: sectionErrors };
    }

    return { valid: true, errors: {} };
  };
  // Validate all sections for the current step
  const handleValidation = () => {
    const sections = stepSchemaSections[step] || [];
    let allValid = true;
    const newErrors = {};

    sections.forEach((sectionKey) => {
      const { valid, errors } = validateForm(sectionKey);
      if (!valid) {
        allValid = false;
        newErrors[sectionKey] = errors;
      }
    });
    // Only update if needed
    setExtraErrors((prev) => {
      const isSame =
        JSON.stringify(prev) === JSON.stringify(allValid ? {} : newErrors);
      return isSame ? prev : allValid ? {} : newErrors;
    });

    return allValid;
  };

  // Transform errors
  const transformErrors = (errors, schema) => {
    return errors
      .filter((error) => {
        const insuranceTerm = formData?.insurance?.insuranceTerm;

        if (
          insuranceTerm?.agreed === "false" &&
          error.property?.includes("insuranceTerm")
        ) {
          return false;
        }

        return ![
          "oneOf",
          "const",
          "dependencies",
          "enum",
          "type",
          "maxItems",
        ].includes(error.name);

        if (error.name === "enum") {
          if (error.property?.includes("agreed")) return true;
          return false;
        }
      })
      .map((error) => {
        const path =
          error.property?.replace(/^\./, "").split(".").filter(Boolean) || [];
        let fieldSchema = schema;
        // Traverse the schema to find the field schema
        for (const key of path) {
          if (fieldSchema?.properties?.[key]) {
            fieldSchema = fieldSchema.properties[key];
          } else if (fieldSchema?.dependencies?.[key]) {
            // for dependencies that inject properties
            const dep = fieldSchema.dependencies[key];
            const oneOfMatch = dep?.oneOf?.find(
              (o) => o.properties?.[key]?.const === true,
            );
            if (oneOfMatch) {
              fieldSchema = oneOfMatch.properties?.[key];
            }
          } else if (fieldSchema?.items) {
            fieldSchema = fieldSchema.items;
          } else {
            fieldSchema = null;
            break;
          }
        }

        if (error.property?.includes("insuranceTerm")) {
          const insuranceTerm = formData?.insurance?.insuranceTerm;
          const insuranceErrorMessages =
            schema?.properties?.insuranceTerm?.errorMessage?.required;

          if (error.name === "anyOf" && error.property?.endsWith(".agreed")) {
            const agreedValue = insuranceTerm?.agreed;
            if (
              agreedValue === null ||
              agreedValue === undefined ||
              agreedValue === "" ||
              agreedValue === "null"
            ) {
              error.message =
                insuranceErrorMessages?.agreed ||
                "Please select any one option for Long Term Care Insurance terms";
              return error;
            }
            return null;
          }

          if (error.name === "required" && error.params?.missingProperty) {
            const missingProperty = error.params.missingProperty;

            switch (missingProperty) {
              case "agreed":
                if (insuranceTerm?.agreed !== "false") {
                  error.message =
                    insuranceErrorMessages?.agreed ||
                    "Please select any one option for Long Term Care Insurance terms";
                  return error;
                }
                return null; // suppress when N/A

              case "policyDeliveryOption":
                if (insuranceTerm?.agreed === "true") {
                  error.message =
                    insuranceErrorMessages?.policyDeliveryOption ||
                    "Please choose how you will provide your Long Term Care Insurance policy.";
                  return error;
                }
                return null;

              case "policyDocuments":
                if (
                  insuranceTerm?.agreed === "true" &&
                  insuranceTerm?.policyDeliveryOption === "upload_now"
                ) {
                  error.message =
                    insuranceErrorMessages?.policyDocuments ||
                    "Please upload your Long Term Care Insurance policy document.";
                  return error;
                }
                return null;

              default:
                return null;
            }
          }
        }

        const dependencyKeys = dependencyArray;
        if (error.name === "required" && error.params?.missingProperty) {
          const missingProperty = error.params.missingProperty;

          const cleanProperty = error.property?.replace(/^\./, "") || "";
          const path = cleanProperty.split(".").filter(Boolean);
          const field =
            missingProperty ||
            path[path.length - 1] ||
            cleanProperty ||
            "Field";
          let fieldSchema = schema;
          let currentFormData = formData;
          for (let i = 0; i < path.length; i++) {
            const key = path[i];

            if (Array.isArray(currentFormData)) {
              const index = parseInt(key, 10);
              if (!isNaN(index)) {
                currentFormData = currentFormData[index];
                fieldSchema = fieldSchema.items;
                continue;
              }
            }
            if (fieldSchema?.properties?.[key]) {
              fieldSchema = fieldSchema.properties[key];
              currentFormData = currentFormData?.[key];
            }
            // Handle dependencies using the utility function
            const result = handleDependencies({
              dependencyKeys,
              fieldSchema,
              formData,
              path,
              field,
              currentFormData,
            });
            fieldSchema = result.fieldSchema;
            currentFormData = result.currentFormData;
          }

          // Final check if it's inside array item
          if (fieldSchema?.items && fieldSchema.items?.properties?.[field]) {
            fieldSchema = fieldSchema.items.properties[field];
          }
          if (fieldSchema?.items?.dependencies && path.includes("contacts")) {
            fieldSchema =
              fieldSchema?.items?.dependencies?.contactRelationship?.oneOf[0]
                ?.properties?.[field];
          }
          if (fieldSchema) {
            const requiredMessage = fieldSchema?.errorMessage?.required;
            let schemaRequiredMessage =
              typeof requiredMessage === "object"
                ? requiredMessage?.[missingProperty]
                : requiredMessage;

            if (!schemaRequiredMessage) {
              schemaRequiredMessage =
                schema?.properties?.insuranceTerm?.errorMessage?.required?.[
                  missingProperty
                ];
            }

            error.message =
              schemaRequiredMessage ||
              fieldSchema?.properties?.[missingProperty]?.errorMessage
                ?.required ||
              `${field} is required`;
          } else {
            error.message = `${field} is required`;
          }
        } else if (error.name === "minLength") {
          const cleanProperty = error.property?.replace(/^\./, "") || "";
          const path = cleanProperty.split(".").filter(Boolean);
          const field = path[path.length - 1] || "Field";

          let fieldSchema = schema;
          let currentFormData = formData;
          for (let i = 0; i < path.length; i++) {
            const key = path[i];

            if (Array.isArray(currentFormData)) {
              const index = parseInt(key, 10);
              if (!isNaN(index)) {
                currentFormData = currentFormData[index];
                fieldSchema = fieldSchema.items;
                continue;
              }
            }
            if (fieldSchema?.properties?.[key]) {
              fieldSchema = fieldSchema.properties[key];
              currentFormData = currentFormData?.[key];
            }

            // Handle dependencies using the utility function
            const result = handleDependencies({
              dependencyKeys,
              fieldSchema,
              formData,
              path,
              field,
              currentFormData,
            });

            fieldSchema = result.fieldSchema;
            currentFormData = result.currentFormData;
          }
          // Final check if it's inside array item
          if (fieldSchema?.items && fieldSchema.items?.properties?.[field]) {
            fieldSchema = fieldSchema.items.properties[field];
          }
          if (fieldSchema?.items?.dependencies && path.includes("contacts")) {
            fieldSchema =
              fieldSchema?.items?.dependencies?.contactRelationship?.oneOf[0]
                ?.properties?.[field];
          }
          if (fieldSchema) {
            error.message =
              fieldSchema?.errorMessage?.minLength ||
              `${field} must be at least ${fieldSchema?.minLength} characters long`;
          } else {
            error.message = `${field} must be at least minimum length`;
          }
        } else if (error.name === "maxLength") {
          const cleanProperty = error.property?.replace(/^\./, "") || "";
          const path = cleanProperty.split(".").filter(Boolean);
          const field = path[path.length - 1] || "Field";

          let fieldSchema = schema;
          let currentFormData = formData;
          for (let i = 0; i < path.length; i++) {
            const key = path[i];

            if (Array.isArray(currentFormData)) {
              const index = parseInt(key, 10);
              if (!isNaN(index)) {
                currentFormData = currentFormData[index];
                fieldSchema = fieldSchema.items;
                continue;
              }
            }
            if (fieldSchema?.properties?.[key]) {
              fieldSchema = fieldSchema.properties[key];
              currentFormData = currentFormData?.[key];
            }

            // Handle dependencies using the utility function
            const result = handleDependencies({
              dependencyKeys,
              fieldSchema,
              formData,
              path,
              field,
              currentFormData,
            });

            fieldSchema = result.fieldSchema;

            currentFormData = result.currentFormData;
          }
          // Final check if it's inside array item
          if (fieldSchema?.items && fieldSchema.items?.properties?.[field]) {
            fieldSchema = fieldSchema.items.properties[field];
          }
          if (fieldSchema?.items?.dependencies && path.includes("contacts")) {
            fieldSchema =
              fieldSchema?.items?.dependencies?.contactRelationship?.oneOf[0]
                ?.properties?.[field];
          }
          if (fieldSchema) {
            error.message =
              fieldSchema?.errorMessage?.maxLength ||
              `${field} must be at least ${fieldSchema?.maxLength} characters long`;
          } else {
            error.message = `${field} must be at least minimum length`;
          }
        } else if (
          [
            "format",
            "minimum",
            "maximum",
            "pattern",
            "multipleOf",
            "type",
            "anyOf",
            "minItems",
            "if",
          ].includes(error.name)
        ) {
          const cleanProperty = error.property?.replace(/^\./, "") || "";
          const path = cleanProperty.split(".").filter(Boolean);
          const field = path[path.length - 1] || cleanProperty || "Field";

          let fieldSchema = schema;
          let currentFormData = formData;
          for (let i = 0; i < path.length; i++) {
            const key = path[i];

            if (Array.isArray(currentFormData)) {
              const index = parseInt(key, 10);
              if (!isNaN(index)) {
                currentFormData = currentFormData[index];
                fieldSchema = fieldSchema.items;
                continue;
              }
            }
            if (fieldSchema?.properties?.[key]) {
              fieldSchema = fieldSchema.properties[key];
              currentFormData = currentFormData?.[key];
            }

            // Handle dependencies using the utility function
            const result = handleDependencies({
              dependencyKeys,
              fieldSchema,
              formData,
              path,
              field,
              currentFormData,
            });

            fieldSchema = result.fieldSchema;
            currentFormData = result.currentFormData;
          }
          // Final array item schema lookup
          if (fieldSchema?.items && fieldSchema.items?.properties?.[field]) {
            fieldSchema = fieldSchema.items.properties[field];
          }
          if (fieldSchema?.items?.dependencies && path.includes("contacts")) {
            fieldSchema =
              fieldSchema?.items?.dependencies?.contactRelationship?.oneOf[0]
                ?.properties?.[field];
          }
          // Final error message assignment
          if (fieldSchema) {
            const title = fieldSchema?.title || field;
            switch (error.name) {
              case "format":
                error.message =
                  fieldSchema?.errorMessage?.format ||
                  `${title} must be a valid ${fieldSchema?.format}`;
                break;
              case "minimum":
                error.message =
                  fieldSchema?.errorMessage?.minimum ||
                  `${title} must be at least ${fieldSchema?.minimum}`;
                break;
              case "maximum":
                error.message =
                  fieldSchema?.errorMessage?.maximum ||
                  `${title} must be at most ${fieldSchema?.maximum}`;
                break;
              case "pattern":
                error.message =
                  fieldSchema?.errorMessage?.pattern ||
                  `${title} is invalid. Please enter a value that matches the required format.`;
                break;
              case "multipleOf":
                error.message =
                  fieldSchema?.errorMessage?.multipleOf ||
                  `${title} must be a valid number.`;
                break;
              case "anyOf":
                error.message =
                  fieldSchema?.errorMessage?.anyOf || `${title} is required.`;
                break;
              case "minItems":
                if (title === "Contacts" || title === "contacts") {
                  fieldSchema = schema;
                  error.message =
                    fieldSchema?.errorMessage?.minItems ||
                    `${fieldSchema?.title || "Field"} is invalid`;
                  break;
                }

                if (error.property?.includes("insuranceTerm.policyDocuments")) {
                  const insuranceTerm = formData?.insurance?.insuranceTerm;
                  const agreedValue = insuranceTerm?.agreed;
                  const isAgreed =
                    agreedValue === true || agreedValue === "true";
                  const isUploadNow =
                    insuranceTerm?.policyDeliveryOption === "upload_now";

                  if (isAgreed && isUploadNow) {
                    error.message =
                      schema?.properties?.insuranceTerm?.errorMessage?.required
                        ?.policyDocuments ||
                      "Please upload your Long Term Care Insurance policy document";
                    return error;
                  } else if (
                    isAgreed &&
                    insuranceTerm?.policyDeliveryOption === ""
                  ) {
                    error.message =
                      schema?.properties?.insuranceTerm?.errorMessage?.required
                        ?.policyDeliveryOption ||
                      "Please indicate how you will provide your Long Term Care Insurance policy";
                    return error;
                  }
                  return null;
                }

                error.message =
                  fieldSchema?.errorMessage?.minItems ||
                  `${fieldSchema?.title || "Field"} is invalid`;
                break;
              case "type":
                if (title === "Contacts") {
                  error.message =
                    fieldSchema?.errorMessage?.minItems ||
                    `${title} must be a valid number.`;
                  break;
                } else {
                  error.message =
                    fieldSchema?.errorMessage?.type ||
                    `${title} must be a valid number.`;
                  break;
                }
            }
          } else {
            // Fallback messages
            switch (error.name) {
              case "format":
                error.message = `${field} must be a valid format`;
                break;
              case "minimum":
                error.message = `${field} must be at least minimum value`;
                break;
              case "maximum":
                error.message = `${field} must be at most maximum value`;
                break;
              case "pattern":
                error.message = `${field} is invalid. Please enter a value that matches the required format.`;
                break;
              case "multipleOf":
                error.message = `${field} must be a valid number.`;
                break;
              case "anyOf":
                error.message =
                  fieldSchema?.errorMessage?.anyOf || `${field} is required.`;
                break;
              case "minItems":
                if (field === "contacts") {
                  fieldSchema = schema;
                }
                error.message =
                  fieldSchema?.errorMessage?.minItems ||
                  `${field} is required.`;
                break;
              case "type":
                error.message = `${field} is invalid`;
                break;
            }
          }
        } else if (fieldSchema) {
          error.message =
            fieldSchema?.errorMessage?.[error.name] ||
            `${fieldSchema?.title || "Field"} is invalid`;
        }

        return error;
      })
      .filter(Boolean);
  };
  // Scroll to first error
  useEffect(() => {
    const scrollToError = () => {
      const firstError = document.querySelector(".Mui-error");
      if (firstError) {
        firstError.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    };
    requestAnimationFrame(scrollToError);
  }, [extraErrors]);

  // Handle form data changes
  const handleChange = ({ formData: newFormData }, sectionKey) => {
    setFormData((prev) =>
      deepMerge(prev, { [sectionKey]: newFormData[sectionKey] }),
    );
  };
  const debouncedHandleChange = useMemo(
    () =>
      debounce((e, key) => {
        const updated = {
          ...formData,
          [key]: e.formData[key],
        };
        const cleaned = cleanDisabledDependents(updated, schemas[step]);

        setFormData(cleaned);
      }, 300),
    [formData, schemas, step],
  );

  const handleNext = async () => {
    const isValid = handleValidation();

    if (isValid && formCustomValidation) {
      // Remove current step from pending list after it's valid
      setPendingSteps((prev) => prev.filter((s) => s !== step));

      // Save valid data and updated pendingSteps to IndexedDB (only for new forms)
      if (!id) {
        try {
          const db = await dbPromise;
          await db.put("formData", {
            id: "clientIntake",
            data: formData,
            pendingSteps: pendingSteps.filter((s) => s !== step),
          });
        } catch (error) {
          console.error("Error saving form data to IndexedDB:", error);
        }
      }

      // Move to the next step
      setStep((prev) => prev + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handlePrevious = () => {
    const isValid = handleValidation();
    if (isValid && formCustomValidation) {
      setStep((prev) => Math.max(prev - 1, 0));
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleReset = async () => {
    setFormData(defaultFormData);
    setFormDataVersion((v) => v + 1);
    setOpenSections((prev) => {
      const openState = {};
      schemas.forEach((s, i) => {
        Object.keys(s.properties || {}).forEach((k) => {
          openState[`${i}-${k}`] = true;
        });
      });
      return openState;
    });
    setStep(0);
    setPendingSteps(
      Array.from({ length: schemas.length }, (_, i) => i).filter(
        (i) => i !== 7 && i !== 11,
      ),
    ); // Reset pendingSteps
    window.scrollTo({ top: 0, behavior: "smooth" });
    try {
      const db = await dbPromise;
      await db.delete("formData", "clientIntake");
    } catch (error) {
      console.error("Error clearing form data from IndexedDB:", error);
    }
  };

  const handleStepClick1 = (i) => {
    if (i === step || !handleValidation()) return;
    setStep(i);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleStepClick = async (targetStep) => {
    if (targetStep === step || !handleValidation() || !formCustomValidation)
      return;
    if (!id) {
      // Redirect to the first pending step before target step if any required step is missed
      const firstPending = pendingSteps.find((s) => s < targetStep);
      if (typeof firstPending !== "undefined") {
        setStep(firstPending);

        requestAnimationFrame(() => {
          const firstError = document.querySelector(".Mui-error");
          if (firstError) {
            firstError.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        });
        return;
      }
    }

    // All required prior steps are complete, allow moving to target step
    setExtraErrors({});
    setStep(targetStep);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const getValueByPath = (obj, path) => {
    const keys = path.split(".");

    let current = obj;

    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];

      if (key === "*") {
        // handle array wildcard
        if (!Array.isArray(current)) return ""; // not an array → treat as missing
        const restPath = keys.slice(i + 1).join(".");
        // check all array items
        return current.map((item) => getValueByPath(item, restPath));
      }

      if (current && current[key] !== undefined) {
        current = current[key];
      } else {
        return ""; // missing key
      }
    }

    return current;
  };

  // Validation function (collects missing fields step-wise)
  const validateRequiredFields = (formData, requiredFieldsValidate) => {
    const missingFieldsByStep = {};

    requiredFieldsValidate.forEach(({ path, label, step, validate }) => {
      const value = getValueByPath(formData, path);

      let isMissing = false;

      if (typeof validate === "function") {
        isMissing = !validate(value, formData);
      } else if (Array.isArray(value)) {
        // Wildcard result: if array empty OR any item is "" → error
        if (
          value.length === 0 ||
          value.some((v) => v === "" || v === null || v === undefined)
        ) {
          isMissing = true;
        }
      } else {
        // normal value
        if (value === "" || value === null || value === undefined) {
          isMissing = true;
        }
      }

      if (isMissing) {
        if (!missingFieldsByStep[step]) {
          missingFieldsByStep[step] = [];
        }
        missingFieldsByStep[step].push(label);
      }
    });

    return missingFieldsByStep;
  };

  const handleSubmit = () => {
    if (!handleValidation()) return;
    const missingFieldsByStep = validateRequiredFields(
      formData,
      requiredFieldsValidate,
    );

    if (Object.keys(missingFieldsByStep).length > 0) {
      const sortedSteps = Object.keys(missingFieldsByStep)
        .map(Number)
        .sort((a, b) => a - b);

      const firstErrorStep = sortedSteps[0];
      const fields = missingFieldsByStep[firstErrorStep] || [];

      if (fields.length > 0) {
        showSnackbar(
          <div>
            <div>Please fill required fields in Step {firstErrorStep}:</div>
            <ul style={{ margin: 0, paddingLeft: "20px" }}>
              {fields.map((f, i) => (
                <li key={i}>{f}</li>
              ))}
            </ul>
          </div>,
          "error",
        );
        return; // stop submit
      }
    }
    const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY;

    const policyDocuments =
      formData?.insurance?.insuranceTerm?.policyDocuments || [];

    const formDataForEncryption = {
      ...formData,
      insurance: {
        ...formData.insurance,
        insuranceTerm: {
          ...formData.insurance?.insuranceTerm,
          policyDocuments: policyDocuments.map(({ name, type, size }) => ({
            name,
            type,
            size,
            // content intentionally excluded
          })),
        },
      },
    };

    // Step 1: Extract values
    const extract = {
      first_name: formData?.clientInformation?.firstName ?? "",
      last_name: formData?.clientInformation?.lastName ?? "",
      dob: formData?.clientInformation?.dateOfBirth ?? "",
      form_data: formDataForEncryption,
    };
    // Step 2: Define paths to skip
    const pathsToSkip = [
      "form_data.additionalInformation.vehiclePreference",
      "form_data.scheduleVariableFlexible.days",
      "form_data.formSubmissionDetails.communityLiaison",
      "form_data.formSubmissionDetails.email",
      "form_data.formSubmissionDetails.referralSource",
    ];

    // Step 3: Encryption logic
    const encryptValues = (obj, pathsToSkip, parentPath = "") => {
      const result = Array.isArray(obj) ? [] : {};

      for (const key in obj) {
        if (!Object.prototype.hasOwnProperty.call(obj, key)) continue;

        const value = obj[key];

        const currentPath = parentPath ? `${parentPath}.${key}` : key;
        const shouldSkip = pathsToSkip.includes(currentPath);

        if (shouldSkip) {
          result[key] = value;
        } else if (
          typeof value === "object" &&
          value !== null &&
          value !== ""
        ) {
          result[key] = encryptValues(value, pathsToSkip, currentPath);
        } else if (value !== null && value !== undefined && value !== "") {
          result[key] = CryptoJS.AES.encrypt(
            String(value),
            ENCRYPTION_KEY,
          ).toString();
        } else {
          if (value !== undefined) {
            result[key] = value;
          }
        }
      }

      return result;
    };

    const encryptedDataObj = encryptValues(extract, pathsToSkip);
    const encryptedFormData = JSON.stringify(encryptedDataObj, null, 2);

    // Step 4: Final payload

    const requestData = new FormData();
    requestData.append("FirstName", extract.first_name);
    requestData.append("LastName", extract.last_name);
    requestData.append("Dob", extract.dob);
    requestData.append("FormData", encryptedFormData);

    if (id) {
      const differences = diffObjects(oldFormData, formData);
      const encryptedUpdateFormdata = encryptValues(differences, pathsToSkip);
      const encryptedStringifyFormData = JSON.stringify(
        encryptedUpdateFormdata,
        null,
        2,
      );
      requestData.append("UnqGuid", id);
      requestData.append("UpdatedBy", getUserdetail?.userId);
      requestData.append("UserId", getUserdetail?.userId);
      requestData.append("UpdatedFormData", encryptedStringifyFormData);
    } else {
      policyDocuments.forEach((doc) => {
        const file = policyFiles.find(
          (f) =>
            f.name === doc.name && f.size === doc.size && f.type === doc.type,
        );
        if (file) {
          requestData.append("Files", file);
        }
      });
      requestData.append(
        "CreatedBy",
        formData?.clientInformation?.firstName ?? "",
      );
      requestData.append("MarketerCode", marketerCode);
    }
    // Step 5: API Call using apiClient
    const requestUrl = id
      ? urlConstant.Submission.UpdateIntakeFormSubmit
      : urlConstant.IntakeForm.IntakeFormSubmit;
    if (!id) {
      trackPromise(
        apiClient
          .post(requestUrl, requestData, {
            authRequired: false,
          })
          .then((response) => {
            if (
              response?.status === "Success" &&
              response?.statusCode === 200
            ) {
              const submissionCode = response?.data || "N/A";
              handleReset();
              navigate("/intakeSuccess", { state: { submissionCode } });
            } else {
              showSnackbar(response?.message, "error");
            }
          })
          .catch((error) => {
            console.error("API Error:", error);
          }),
      );
    } else {
      trackPromise(
        apiClient
          .put(requestUrl, requestData, {
            authRequired: true,
          })
          .then((response) => {
            if (response?.status !== dataConstant.APIStatus.Success) {
              showSnackbar(response?.message, "error");
            } else {
              showSnackbar(response?.message, "success");
              setTimeout(() => {
                navigate("/submission");
              }, 2000);
            }
          })
          .catch((error) => {
            console.error("API Error:", error);
          }),
      );
    }
  };

  const handleDrag = (e, type) => {
    const stepper = stepperRef.current;
    if (type === "start") {
      stepper.isDragging = true;
      stepper.startX = e.pageX || e.touches?.[0]?.pageX;
      stepper.scrollLeft = stepper.scrollLeft;
    } else if (type === "move" && stepper.isDragging) {
      const x = e.pageX || e.touches?.[0]?.pageX;
      const walk = (x - stepper.startX) * 1.5;
      stepper.scrollLeft = stepper.scrollLeft - walk;
    } else if (type === "end") {
      stepper.isDragging = false;
    }
  };
  const scrollStepper = (dir) => {
    stepperRef.current.scrollBy({ left: dir * 200, behavior: "smooth" });
  };

  const currentSchema = schemas[step];
  const saveMarketerCode = (code) => {
    setMarketerCode(code);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container
        maxWidth="md"
        sx={{
          py: 3,
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
        }}
      >
        {/* {!id && (
          <> */}
        {!id && (
          <img
            src="/Cambridge-Caregivers-Logo-2 1.svg"
            alt="Logo"
            style={{ width: "25%", margin: "auto", display: "block" }}
          />
        )}
        <Typography variant="h2" align="center" color="default.main" my={1}>
          Engagement Form
        </Typography>
        {!id && (
          <Typography
            variant="body2"
            align="center"
            color="default.light"
            mb={1}
          >
            Thank you for your time and interest in working with Cambridge
            Caregivers. If you have any questions, please feel free to contact
            your community liaison directly.
          </Typography>
        )}
        <Box my={1} display="flex" alignItems="center" gap={2}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ height: 10, borderRadius: 5, flex: "1 1 0" }}
          />
          <Typography
            variant="caption"
            align="center"
            display="block"
            color="default.light"
          >
            {Math.round(progress)}% complete
          </Typography>
        </Box>
        {/* </>
        )} */}

        <Box
          position="sticky"
          top={0}
          pt={2}
          zIndex={9}
          mb={4}
          bgcolor="common.white"
        >
          <IconButton
            onClick={() => scrollStepper(-1)}
            sx={{
              position: "absolute",
              left: 0,
              top: "6px",
              zIndex: 1,
              backgroundColor: lightPalette.primary.lightest1,
              color: lightPalette.common.black,
              boxShadow: "none",
              transition: "0.4s all ease-in-out",

              ":hover": {
                backgroundColor: lightPalette.primary.main,
                color: lightPalette.common.white,
              },
            }}
          >
            <IconChevronLeft />
          </IconButton>

          <Box
            ref={stepperRef}
            sx={{
              overflowX: "auto",
              display: "flex",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
            onMouseDown={(e) => handleDrag(e, "start")}
            onMouseMove={(e) => handleDrag(e, "move")}
            onMouseUp={() => handleDrag(null, "end")}
            onMouseLeave={() => handleDrag(null, "end")}
            onTouchStart={(e) => handleDrag(e, "start")}
            onTouchMove={(e) => handleDrag(e, "move")}
            onTouchEnd={() => handleDrag(null, "end")}
          >
            <Stepper
              activeStep={step}
              alternativeLabel
              sx={{ flexGrow: 1, width: "100%", display: "flex" }}
            >
              {stepTitles.map((label, i) => (
                <Step
                  key={label}
                  onClick={() => handleStepClick(i)}
                  sx={{
                    cursor: "pointer",
                    flex: "0 0 auto",
                    width: { xs: "35%", md: "20%" },
                  }}
                >
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          <IconButton
            onClick={() => scrollStepper(1)}
            sx={{
              position: "absolute",
              right: 0,
              top: "6px",
              zIndex: 1,
              backgroundColor: lightPalette.primary.lightest1,
              color: lightPalette.common.black,
              boxShadow: "none",
              transition: "0.4s all ease-in-out",

              ":hover": {
                backgroundColor: lightPalette.primary.main,
                color: lightPalette.common.white,
              },
            }}
          >
            <IconChevronRight />
          </IconButton>
        </Box>

        {Object.entries(currentSchema.properties).map(([key, value]) => {
          const propertyCount = Object.keys(
            currentSchema.properties || {},
          ).length;
          const sectionId = `${step}-${key}`;
          const isCollapsible = step != 10 || step != 11;
          const formElement = (
            <Form
              schema={{
                type: "object",
                properties: { [key]: value },
                dependencies: schemas[step].dependencies?.[key],
              }}
              uiSchema={{ [key]: currentUiSchemas[key] || {} }}
              formData={{ [key]: formData[key] ?? {} }}
              onChange={(e) => debouncedHandleChange(e, key)}
              extraErrors={extraErrors[key] || {}}
              validator={validator}
              showErrorList={false}
              widgets={widgets}
              fields={fields}
              formContext={{
                ...formContext,
                fullFormData: formData,
                setFormData: setFormData,
                setFormDataVersion: setFormDataVersion,
                formDataVersion: formDataVersion,
                setCustomValidation: setCustomValidation,
                formCustomValidation: formCustomValidation,
              }}
              key={`${step}-${key}-${formDataVersion}`}
              templates={{ ObjectFieldTemplate }}
              transformErrors={(errors) => transformErrors(errors, value)}
              liveValidate={Object.keys(extraErrors).length > 0}
              // Enable omitExtraData and liveOmit for all steps except 1, 5, and 9
              // This removes any form fields not in the schema during change events
              {...(step !== 1 && step !== 9 && step !== 5
                ? { omitExtraData: true, liveOmit: true }
                : {})}
              className="rjsf cm-mt-3 cm-flex-1"
            >
              <></>
            </Form>
          );

          return (
            <Box flex="1 1 0">
              {isCollapsible ? (
                <FormCard sx={{ p: { xs: 2, md: 3 } }}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box>
                      <Typography variant="h4" color="default.main">
                        {value.title}
                      </Typography>
                      {value.description && (
                        <Typography variant="body2" color="default.light">
                          {value.description}
                        </Typography>
                      )}
                    </Box>
                    <IconButton
                      onClick={() =>
                        setOpenSections((prev) => ({
                          ...prev,
                          [sectionId]: !prev[sectionId],
                        }))
                      }
                    >
                      {openSections[sectionId] ? (
                        <ExpandLess />
                      ) : (
                        <ExpandMore />
                      )}
                    </IconButton>
                  </Box>
                  <Collapse in={openSections[sectionId] ?? true}>
                    {formElement}
                  </Collapse>
                </FormCard>
              ) : (
                <FormCard sx={{ p: { xs: 2, md: 3 } }}>{formElement}</FormCard>
              )}
            </Box>
          );
        })}

        <Box
          display="flex"
          justifyContent="space-between"
          flexWrap={{ xs: "wrap", md: "nowrap" }}
          position="sticky"
          bottom={0}
          gap={2}
          p={2}
          zIndex={1}
          borderRadius={2}
          component={Paper}
        >
          <Button
            variant="outlined"
            onClick={handlePrevious}
            startIcon={<ArrowBack />}
            disabled={step === 0} // Disable on first step
          >
            Previous
          </Button>

          {!id && (
            <Button
              variant="outlined"
              onClick={() => setConfirmOpen(true)}
              startIcon={<RestartAlt />}
              sx={{
                order: { xs: -1, sm: 0 },
                width: { xs: "100%", sm: "auto" },
              }}
            >
              Clear Form
            </Button>
          )}

          {step === schemas.length - 1 ? (
            <Button variant="contained" color="success" onClick={handleSubmit}>
              Submit
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              endIcon={<ArrowForward />}
            >
              Next
            </Button>
          )}
        </Box>

        <Dialog
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          aria-labelledby="reset-dialog-title"
          aria-describedby="reset-dialog-description"
          maxWidth="xs"
        >
          <DialogTitle id="reset-dialog-title">Are you sure?</DialogTitle>
          <DialogContent>
            <DialogContentText
              typography="h6"
              color="default.main"
              id="reset-dialog-description"
            >
              This will clear all your entered information and return you to
              step 1. This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              size="small"
              onClick={() => setConfirmOpen(false)}
              color="primary"
              variant="outlined"
            >
              Cancel
            </Button>
            {!id && (
              <Button
                size="small"
                onClick={() => {
                  setConfirmOpen(false);
                  handleReset();
                }}
                color="primary"
                variant="contained"
              >
                Clear Form
              </Button>
            )}
          </DialogActions>
        </Dialog>
        <Dialog
          open={isCodeValidPopupOpen}
          onClose={(event, reason) => {
            if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
              setIsCodeValidPopupOpen(false);
            }
          }}
          aria-labelledby="reset-dialog-title"
          aria-describedby="reset-dialog-description"
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle id="reset-dialog-title">Password</DialogTitle>

          <DialogContent>
            <MarketerCodeVerify
              saveMarketerCode={saveMarketerCode}
              setIsCodeValidPopupOpen={setIsCodeValidPopupOpen}
            />
          </DialogContent>
        </Dialog>
      </Container>
      <CommonSnackBar snackObj={snack} closeSnack={closeSnack} />
    </LocalizationProvider>
  );
};

export default ClientIntakeFrom;
